// frontend/src/components/StreamingMessageRenderer.js - NEW FILE
import React, { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import StreamingCursor from './StreamingCursor';

/**
 * Extract complete code blocks from content
 */
function extractCompleteCodeBlocks(content) {
    const pattern = /```[\s\S]*?```/g;
    const blocks = [];
    let match;
    
    while ((match = pattern.exec(content)) !== null) {
        blocks.push({
            content: match[0],
            start: match.index,
            end: match.index + match[0].length
        });
    }
    
    return blocks;
}

/**
 * Split content into stable (complete code blocks) and streaming (rest)
 */
function splitStreamingContent(content) {
    if (!content) return { parts: [] };

    const codeBlocks = extractCompleteCodeBlocks(content);
    
    if (codeBlocks.length === 0) {
        // No complete code blocks - render everything as streaming
        return {
            parts: [{ type: 'streaming', content, key: 'stream-0' }]
        };
    }

    const parts = [];
    let lastIndex = 0;

    codeBlocks.forEach((block, index) => {
        // Content before this code block
        if (block.start > lastIndex) {
            const beforeContent = content.substring(lastIndex, block.start);
            parts.push({
                type: 'streaming',
                content: beforeContent,
                key: `stream-${index}`
            });
        }

        // The complete code block (stable)
        parts.push({
            type: 'stable',
            content: block.content,
            key: `code-${index}`
        });

        lastIndex = block.end;
    });

    // Remaining content after last code block
    if (lastIndex < content.length) {
        const remaining = content.substring(lastIndex);
        parts.push({
            type: 'streaming',
            content: remaining,
            key: 'stream-final'
        });
    }

    return { parts };
}

/**
 * Stable code block component - never re-renders once mounted
 */
const StableCodeBlock = React.memo(({ content, components }) => {
    return (
        <Box>
            <ReactMarkdown
                components={components}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                skipHtml={false}
                linkTarget="_blank"
            >
                {content}
            </ReactMarkdown>
        </Box>
    );
}, (prev, next) => prev.content === next.content);

StableCodeBlock.displayName = 'StableCodeBlock';

/**
 * Streaming content component - renders markdown or falls back to plaintext
 */
const StreamingContent = React.memo(({ content, components, showCursor }) => {
    // Try to render as markdown, but catch incomplete markdown gracefully
    const renderContent = () => {
        try {
            return (
                <ReactMarkdown
                    components={components}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    skipHtml={false}
                    linkTarget="_blank"
                >
                    {content}
                </ReactMarkdown>
            );
        } catch (error) {
            // Fallback to plaintext if markdown is broken
            return (
                <Box
                    as="pre"
                    whiteSpace="pre-wrap"
                    fontFamily="inherit"
                    fontSize="inherit"
                >
                    {content}
                </Box>
            );
        }
    };

    return (
        <Box position="relative">
            {renderContent()}
            {showCursor && <StreamingCursor />}
        </Box>
    );
}, (prev, next) => {
    return prev.content === next.content && prev.showCursor === next.showCursor;
});

StreamingContent.displayName = 'StreamingContent';

/**
 * Main streaming renderer - splits content intelligently
 */
const StreamingMessageRenderer = ({ content = '', components }) => {
    const { parts } = useMemo(() => {
        return splitStreamingContent(content);
    }, [content]);

    return (
        <Box>
            {parts.map((part, index) => {
                const isLastPart = index === parts.length - 1;
                
                if (part.type === 'stable') {
                    return (
                        <StableCodeBlock
                            key={part.key}
                            content={part.content}
                            components={components}
                        />
                    );
                }

                return (
                    <StreamingContent
                        key={part.key}
                        content={part.content}
                        components={components}
                        showCursor={isLastPart}
                    />
                );
            })}
        </Box>
    );
};

export default React.memo(StreamingMessageRenderer);